import { useState } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Loader2, ShieldCheck, Ticket, Check, X } from "lucide-react";
import { toast } from "sonner";

const fmtPrice = (n) =>
  new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(n);

/**
 * CheckoutModal
 *
 * Guest-friendly two-step checkout:
 *   - collects buyer identity + billing address (required by Stripe for EU)
 *   - calls POST /api/checkout/stripe and redirects to the hosted checkout URL
 *
 * Props:
 *   open              boolean
 *   onOpenChange      (o: boolean) => void
 *   courseIds         string[]
 *   total             number (EUR, informative — server recomputes)
 */
export default function CheckoutModal({ open, onOpenChange, courseIds, total }) {
  const [form, setForm] = useState({
    email: "",
    first_name: "",
    last_name: "",
    phone: "",
    tax_id: "",
    address: "",
    postal_code: "",
    city: "",
    country: "ES",
  });
  const [submitting, setSubmitting] = useState(false);
  const [codeInput, setCodeInput] = useState("");
  const [appliedCode, setAppliedCode] = useState(null); // { code, kind, value }
  const [validatingCode, setValidatingCode] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Local estimate of the discount the code would apply — the server
  // re-computes authoritatively, but this gives the user immediate feedback.
  // For percent: applied to the current `total` (approximation, doesn't
  // subtract the package-portion — server enforces the "only loose courses"
  // rule). For fixed: capped by `total`.
  const codeAmountEstimate = (() => {
    if (!appliedCode) return 0;
    if (appliedCode.kind === "percent") return Math.round(total * appliedCode.value) / 100;
    return Math.min(total, appliedCode.value);
  })();
  const finalTotal = Math.max(0, total - codeAmountEstimate);

  const applyCode = async () => {
    const raw = (codeInput || "").trim().toUpperCase();
    if (!raw) return;
    setValidatingCode(true);
    try {
      const { data } = await api.post("/public/discount-codes/validate", {
        code: raw,
        course_ids: courseIds,
      });
      setAppliedCode(data);
      toast.success(`Código ${data.code} aplicado`);
    } catch (e) {
      setAppliedCode(null);
      toast.error(e?.response?.data?.detail || "No se pudo validar el código");
    } finally { setValidatingCode(false); }
  };

  const removeCode = () => {
    setAppliedCode(null);
    setCodeInput("");
  };

  const validate = () => {
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      return "Email inválido";
    if (!form.first_name.trim()) return "Nombre obligatorio";
    if (!form.last_name.trim()) return "Apellidos obligatorios";
    if (!form.address.trim() || !form.postal_code.trim() || !form.city.trim())
      return "Dirección, código postal y ciudad son obligatorios para la factura";
    return null;
  };

  const submit = async () => {
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/checkout/stripe", {
        course_ids: courseIds,
        customer: form,
        origin_url: window.location.origin,
        discount_code: appliedCode?.code || null,
      });
      // Redirect to Stripe hosted checkout
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo iniciar el pago");
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="checkout-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-brand-ink">Datos de facturación</DialogTitle>
          <DialogDescription className="text-xs text-gray-500">
            {courseIds.length} curso{courseIds.length === 1 ? "" : "s"} · Total{" "}
            <span className="font-mono-data text-brand-ink">{fmtPrice(total)}</span>. Tras el pago te
            crearemos una cuenta automáticamente si no existe.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 mt-2">
          <div>
            <Label htmlFor="email">Email *</Label>
            <Input
              id="email"
              type="email"
              value={form.email}
              onChange={(e) => set("email", e.target.value)}
              placeholder="tu@correo.com"
              data-testid="checkout-email"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="first_name">Nombre *</Label>
              <Input
                id="first_name"
                value={form.first_name}
                onChange={(e) => set("first_name", e.target.value)}
                data-testid="checkout-first-name"
              />
            </div>
            <div>
              <Label htmlFor="last_name">Apellidos *</Label>
              <Input
                id="last_name"
                value={form.last_name}
                onChange={(e) => set("last_name", e.target.value)}
                data-testid="checkout-last-name"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="phone">Teléfono</Label>
              <Input
                id="phone"
                value={form.phone}
                onChange={(e) => set("phone", e.target.value)}
                placeholder="+34 …"
                data-testid="checkout-phone"
              />
            </div>
            <div>
              <Label htmlFor="tax_id">DNI / NIE / CIF</Label>
              <Input
                id="tax_id"
                value={form.tax_id}
                onChange={(e) => set("tax_id", e.target.value)}
                placeholder="Opcional"
                data-testid="checkout-tax-id"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="address">Dirección *</Label>
            <Input
              id="address"
              value={form.address}
              onChange={(e) => set("address", e.target.value)}
              placeholder="Calle, número, piso…"
              data-testid="checkout-address"
            />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label htmlFor="postal_code">C.P. *</Label>
              <Input
                id="postal_code"
                value={form.postal_code}
                onChange={(e) => set("postal_code", e.target.value)}
                data-testid="checkout-postal-code"
              />
            </div>
            <div className="col-span-2">
              <Label htmlFor="city">Ciudad *</Label>
              <Input
                id="city"
                value={form.city}
                onChange={(e) => set("city", e.target.value)}
                data-testid="checkout-city"
              />
            </div>
          </div>
        </div>

        <div className="mt-4 pt-3 border-t border-gray-100" data-testid="checkout-code-block">
          <Label className="text-xs flex items-center gap-1.5">
            <Ticket className="w-3.5 h-3.5" /> Código de descuento (opcional)
          </Label>
          {appliedCode ? (
            <div
              className="flex items-center justify-between gap-2 mt-1.5 bg-emerald-50 border border-emerald-200 rounded px-3 py-2"
              data-testid="checkout-code-applied"
            >
              <div className="flex items-center gap-2 text-sm text-emerald-800">
                <Check className="w-4 h-4" />
                <span className="font-mono-data font-semibold">{appliedCode.code}</span>
                <span className="text-xs">
                  ({appliedCode.kind === "percent" ? `${appliedCode.value}% descuento` : `${appliedCode.value.toFixed(2)} € descuento`})
                </span>
              </div>
              <button
                type="button"
                onClick={removeCode}
                className="text-emerald-800 hover:text-emerald-900"
                data-testid="checkout-code-remove"
                aria-label="Quitar código"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="flex gap-2 mt-1.5">
              <Input
                value={codeInput}
                onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
                placeholder="SIMA20"
                maxLength={32}
                className="uppercase font-mono-data"
                data-testid="checkout-code-input"
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyCode(); } }}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={applyCode}
                disabled={validatingCode || !codeInput.trim()}
                data-testid="checkout-code-apply"
              >
                {validatingCode ? <Loader2 className="w-4 h-4 animate-spin" /> : "Aplicar"}
              </Button>
            </div>
          )}
          {appliedCode && (
            <p className="text-[11px] text-gray-500 mt-1.5">
              El descuento final lo calcula el servidor sobre los cursos aplicables. Estimación:{" "}
              <span className="font-mono-data text-emerald-700">− {codeAmountEstimate.toFixed(2).replace(".", ",")} €</span>.
            </p>
          )}
        </div>

        <div className="flex items-center gap-2 mt-3 text-[11px] text-gray-500">
          <ShieldCheck className="w-3.5 h-3.5 text-brand" />
          Pago procesado por Stripe · Conexión cifrada · No guardamos tus datos de pago
        </div>

        <DialogFooter className="mt-3">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancelar
          </Button>
          <Button
            onClick={submit}
            disabled={submitting}
            className="bg-brand hover:bg-brand-hover text-white"
            data-testid="submit-checkout-btn"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Redirigiendo…
              </>
            ) : (
              <>Pagar {fmtPrice(finalTotal)}</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
